import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import type { PriceId } from '@iheartjane/types';

import { getDefaultWeight } from '@jane/bloom/viewModels/buildProductCardProps';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';
import type { AlgoliaProduct } from '@jane/search/types';

/**
 * Everything an add-to-bag from the compare modal needs,
 * captured when user clicked the compare toggle.
 */
export interface CompareProductPayload {
  defaultWeight: PriceId;
  hasMultipleWeights: boolean;
  /**
   * `SmartSortProduct.toJSON()`.
   * Null for products that were not surfaced by a placement.
   */
  smartProductJson: string | null;
  /**
   * The product view model as it was at selection time.
   */
  trackingProduct: ProductSearchResultViewModel;
}

interface BuildCompareProductPayloadArgs {
  product: ProductSearchResultViewModel;
  smartProductInstance?: SmartSortProduct<AlgoliaProduct>;
}

export function buildCompareProductPayload({
  product,
  smartProductInstance,
}: BuildCompareProductPayloadArgs): CompareProductPayload {
  return {
    defaultWeight: getDefaultWeight(product) as PriceId,
    hasMultipleWeights: product.hasMultipleWeights,
    smartProductJson: smartProductInstance?.toJSON() ?? null,
    trackingProduct: product,
  };
}

/**
 * Verifies the compare item's payload. Also defence against obsolete/unexpected payload.
 */
export function isCompareProductPayload(
  value: unknown
): value is CompareProductPayload {
  if (!value || typeof value !== 'object') return false;
  const payload = value as Record<string, unknown>;
  return (
    typeof payload['defaultWeight'] === 'string' &&
    typeof payload['hasMultipleWeights'] === 'boolean' &&
    typeof payload['trackingProduct'] === 'object' &&
    payload['trackingProduct'] !== null
  );
}
